import { useState, useEffect } from "react";

export default function Poll({ value }) {
  const pollData = value?.poll;
  const options = pollData?.options || [];
  const pollId = pollData?._id;
  const [votes, setVotes] = useState({});
  const [hasVoted, setHasVoted] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeVotes = async () => {
      if (!pollId) {
        setIsLoading(false);
        return;
      }
      try {
        setError(null);
        const response = await fetch(`/api/interactions/polls/${encodeURIComponent(pollId)}`);
        if (!response.ok) throw new Error(`Poll API error: ${response.status}`);
        const data = await response.json();
        setVotes(data.votes || {});
        const hasUserVoted = localStorage.getItem(`poll-${pollId}`);
        if (hasUserVoted) {
          setHasVoted(true);
          setSelectedOption(localStorage.getItem(`poll-${pollId}-option`));
        }
      } catch (error) {
        console.error("Error initializing votes:", error);
        setError("Failed to load poll data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    if (options.length > 0 && pollId) {
      initializeVotes();
    } else {
      setIsLoading(false);
    }
  }, [pollId, options]);

  const handleVote = async (option) => {
    if (!hasVoted && pollId) {
      try {
        setError(null);
        const response = await fetch(`/api/interactions/polls/${encodeURIComponent(pollId)}/votes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ option }),
        });
        if (!response.ok) throw new Error(`Vote API error: ${response.status}`);
        const result = await response.json();
        setVotes(result.votes || {});
        setHasVoted(true);
        setSelectedOption(option);
        localStorage.setItem(`poll-${pollId}`, "voted");
        localStorage.setItem(`poll-${pollId}-option`, option);
      } catch (error) {
        console.error("Error saving vote:", error);
        setError("Failed to submit vote. Please try again.");
      }
    }
  };

  const getPercentage = (votes, total) => {
    if (total === 0) return 0;
    return Math.round((votes / total) * 100);
  };

  // Calculate the actual width for the background based on the vote percentage
  // Only 40% of the width is distributed according to votes
  const getVoteDisplayWidth = (votePercentage) => {
    return 60 + votePercentage * 0.4; // 60% base + up to 40% based on votes
  };

  if (!pollData || !options.length) {
    return (
      <div className='text-center text-gray-500'>
        Žiadne možnosti v ankete k dispozícii
      </div>
    );
  }

  if (error) {
    return (
      <div className='my-8 flex justify-center w-full'>
        <div className='w-full max-w-[600px] p-6 bg-white rounded-lg shadow-md'>
          <p className='text-red-500 text-center'>{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='my-8 flex justify-center w-full'>
        <div className='w-full max-w-[600px] p-6 bg-white rounded-lg shadow-md text-center'>
          <div className='animate-pulse'>Načítavam anketu...</div>
        </div>
      </div>
    );
  }

  const totalVotes = Object.values(votes).reduce(
    (sum, count) => sum + count,
    0
  );

  return (
    <div className='flex justify-center w-full'>
      <div className='w-full max-w-[400px] p-4 border-gray-300 border rounded-xs bg-white my-8 relative'>
        <img
          src='https://storage.googleapis.com/inaq_sk_bg_video/inaq_blog_logo_anketa.png'
          alt='INAQ Poll Logo'
          className='absolute -top-2 right-1 w-20 h-20 object-contain inaq-poll-img'
        />
        <h3 className='poll-title mx-2 font-source !text-[17px] mb-4 mt-8 leading-10'>
          {pollData.question}
        </h3>
        <div className='space-y-3'>
          {options.map((option) => (
            <button
              key={option}
              onClick={() => handleVote(option)}
              disabled={hasVoted}
              className={`w-full pl-3 py-2 text-left rounded-xs transition-all relative font-source text-[12px]  overflow-hidden`}
            >
              {/* Background for voted options - with minimum 60% width */}
              {hasVoted && (
                <div
                  className={`absolute inset-0 left-0 top-0 h-full transition-all ${
                    selectedOption === option ? "bg-inaqgold" : "bg-gray-100"
                  }`}
                  style={{
                    width: `${getVoteDisplayWidth(getPercentage(votes[option], totalVotes))}%`,
                    zIndex: 0,
                  }}
                />
              )}

              <div className='relative z-10 flex justify-between items-start min-h-[24px]'>
                <span
                  className={`break-words pr-2 ${
                    hasVoted
                      ? selectedOption === option
                        ? "text-white"
                        : "text-inaqgold"
                      : "text-inaqgold"
                  }`}
                >
                  {option}
                </span>

                {hasVoted && (
                  <span
                    className={`shrink-0 pl-1 ${
                      selectedOption === option
                        ? "text-inaqgold"
                        : "text-gray-500"
                    }`}
                  >
                    {getPercentage(votes[option], totalVotes)}%
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
        {hasVoted && (
          <p className='mt-4 text-sm text-gray-500 text-center'>
            Celkovo hlasov: {totalVotes}
          </p>
        )}
        {pollData.caption && (
          <p className='text-center text-gray-500 mt-4'>{pollData.caption}</p>
        )}
      </div>
    </div>
  );
}
